import { z } from 'zod';
import { defineIndexPath, definePath } from '../../router/definePath';

const account = definePath('authenticated', { de: '/konto', en: '/account' });
const credentials = definePath('authenticated', { de: '/konto/zugangsdaten', en: '/account/credentials' });

export const AccountPaths = {
  /** `/account` layout parent — owns the "Konto" breadcrumb root for every page under /account. */
  account,
  /** Index child of `account` — redirects to /home so direct visits to /account don't render an empty layout. */
  accountIndex: defineIndexPath(account),
  privacyOverview: definePath('authenticated', { de: '/konto/datenschutz', en: '/account/privacy' }),
  credentials,
  /** Index child of `credentials` — renders the read view at the credentials URL. */
  credentialsIndex: defineIndexPath(credentials),
  updateEmail: definePath('authenticated', {
    de: '/konto/zugangsdaten/email-aendern',
    en: '/account/credentials/update-email',
  }),
  updatePassword: definePath('authenticated', {
    de: '/konto/zugangsdaten/passwort-aendern',
    en: '/account/credentials/update-password',
  }),
  /**
   * Public activation-code page ("Mit Aktivierungs-Code anmelden"). The optional `token` is the
   * sales token from the welcome mail / device-pairing flow — when present it pre-fills the token
   * field. Absent it, the user enters their username and token manually. Unlike the other
   * WWP-731 tokens, this one is hex (not a JWT), so an all-digit or `1e5`-shaped value is
   * plausible and the router's default `parseSearch` coerces it to a JS number before this schema
   * runs. The union recovers that case as long as the number round-trips exactly
   * (`z.number().int()` in zod v4 requires a safe integer, ~16 digits); beyond that, precision is
   * already lost upstream, so `.catch()` falls back to manual entry instead of forwarding a
   * corrupted token.
   */
  activation: definePath(
    'public',
    { de: '/konto/aktivierung', en: '/account/activation' },
    {
      search: z.object({
        token: z
          .union([z.string(), z.number().int()])
          .transform((value) => (typeof value === 'number' ? String(value) : value))
          .optional()
          .catch(undefined),
      }),
    },
  ),
  personalData: definePath(
    'authenticated',
    { de: '/konto/kundendaten', en: '/account/personal-data' },
    { search: z.object({ edit: z.boolean().optional() }) },
  ),
} as const;
